import React, { useContext, useState, useEffect, useMemo } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	IGetDishRestaurantProperties,
	IGetDishRestaurantPropertiesArguments,
	IGetCategoriesShortResponse,
	IGetCategoriesShort,
	IGetDishInfoV2,
	IItemForGetShortInfoWithStatus,
	IAssignDishToSubcategories,
	IAssignDishToSubcategoriesValidateMetadata,
} from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { convertUndefinedFields } from 'utils/helpers';
import { getCategoriesShortInfoV2 } from 'backend/categories';
import {
	getDishInfoV2,
	getDishRestaurantProperties,
	assignDishToSubcategoriesValidate,
	assignDishToSubcategories,
} from 'backend/dishes';
import { Form } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { preventDefaultForEnterKey } from 'utils/helpers';
import { ModalContent } from 'components/ModalWindows/ModalContent';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import Text from 'components/Text/Text';
import Button from 'components/Button/Button';
import breadcrumbs from 'images/breadcrumbs.svg';
import { CategorySubcategoryBlock } from './CategorySubcategoryBlock';
import { RestaurantsBlock } from './RestaurantsBlock';
import { ValidationInfo } from './ValidationInfo';
import style from './index.module.scss';

interface ISelectedCategorySubcategory {
	index: number;
	selectedCategoryId: Nullable<string>;
	uniqueId: string;
	selectedSubcategoryIds: string[];
}

interface IProps {
	onCloseModal: () => void;
	onFailureEvent?: () => void;
	onShowError: () => void;
	dishId: string;
	IDSelectedRestaurant: string;
}

export const ModalAssignDishToSubcategoryOnRestaurant: React.FC<IProps> = ({
	onCloseModal,
	onFailureEvent,
	onShowError,
	dishId,
	IDSelectedRestaurant,
}) => {
	const [form] = Form.useForm();
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [step, setStep] = useState<'step1' | 'step2' | 'validationInfo'>('step1');
	const [validationInfo, setValidationInfo] = useState<Nullable<IAssignDishToSubcategoriesValidateMetadata>>(null);
	const [selectedCategoriesSubcategories, setSelectedCategoriesSubcategories] = useState<
		ISelectedCategorySubcategory[]
	>([]);
	const [isApplyToOne, toggleApplyToOne] = useState<boolean>(false);
	const [fetchedRestaurants, setFetchedRestaurants] = useState<IItemForGetShortInfoWithStatus[]>([]);
	const [selectedRestaurants, setSelectedRestaurants] = useState<string[]>([]);
	const { response: dishInfoResponse, loading: dishInfoLoading } = useFetchData<IGetDishInfoV2, string>(
		getDishInfoV2,
		dishId,
	);
	const { response: restaurantPropertiesResponse, loading: restaurantPropertiesLoading } = useFetchData<
		IGetDishRestaurantProperties,
		IGetDishRestaurantPropertiesArguments
	>(getDishRestaurantProperties, {
		restaurantId: IDSelectedRestaurant,
		dishId,
	});
	const { response: allCategoriesResponse, loading: allCategoriesLoading } = useFetchData<
		IGetCategoriesShortResponse,
		IGetCategoriesShort
	>(getCategoriesShortInfoV2, { restaurantIds: [IDSelectedRestaurant] });

	useEffect(() => {
		dishInfoLoading || restaurantPropertiesLoading || allCategoriesLoading ? showLoader() : hideLoader();
	}, [dishInfoLoading, restaurantPropertiesLoading, allCategoriesLoading]);

	useEffect(() => {
		if (restaurantPropertiesResponse) {
			const categoriesSubcategories = restaurantPropertiesResponse.data.categories.map((category, index) => ({
				index,
				selectedCategoryId: category.id,
				uniqueId: uuidv4(),
				selectedSubcategoryIds: category.subCategories.map((subCategory) => subCategory.id),
			}));
			setSelectedCategoriesSubcategories(categoriesSubcategories);
		}
	}, [restaurantPropertiesResponse]);

	useEffect(() => {
		form.setFieldsValue({ selectedRestaurants });
		selectedCategoriesSubcategories.forEach((el) => {
			form.setFieldsValue({
				[`IDSelectedCategory-${el.index}`]: el.selectedCategoryId,
				[`IDsSelectedSubcategories-${el.index}`]: el.selectedSubcategoryIds,
			});
		});
	}, [selectedRestaurants, selectedCategoriesSubcategories]);

	const translationOfConfirmBtn = useMemo(() => {
		if (step === 'step1') {
			return 'nextStepButton';
		} else if (step === 'step2') {
			return 'saveButton';
		}
		return 'okButton';
	}, [step]);

	const customerName = useMemo(() => {
		return dishInfoResponse ? dishInfoResponse?.data.customer.name : '-';
	}, [dishInfoResponse]);

	const restaurantName = useMemo(() => {
		return dishInfoResponse
			? dishInfoResponse?.data.restaurants.find((restaurant) => restaurant.id === IDSelectedRestaurant)?.name
			: '-';
	}, [dishInfoResponse]);

	const dishName = useMemo(() => {
		return dishInfoResponse ? dishInfoResponse.data.name : '-';
	}, [dishInfoResponse]);

	const submit = async () => {
		try {
			const restaurantIds = [IDSelectedRestaurant, ...selectedRestaurants];
			const assignments: IAssignDishToSubcategories['assignments'] = selectedCategoriesSubcategories.map(
				(categorySubcategory) => ({
					categoryId: categorySubcategory.selectedCategoryId || '',
					subCategoryIds: categorySubcategory.selectedSubcategoryIds,
				}),
			);
			const convertedData: IAssignDishToSubcategories = convertUndefinedFields({
				dishId,
				restaurantIds,
				assignments,
			});
			showLoader();
			const response = await assignDishToSubcategoriesValidate(convertedData);
			if (response.data.success) {
				await assignDishToSubcategories(convertedData);
				onCloseModal();
			} else {
				setStep('validationInfo');
				setValidationInfo(response.data.metadata);
			}
		} catch (err) {
			onShowError();
		} finally {
			hideLoader();
		}
	};

	const handleAddCategory = () => {
		setSelectedCategoriesSubcategories((state) => [
			...state,
			{ index: state.length, selectedCategoryId: null, uniqueId: uuidv4(), selectedSubcategoryIds: [] },
		]);
	};

	const handleOnConsentEvent = async () => {
		if (step === 'step1') {
			await form.validateFields();
			setStep('step2');
		} else if (step === 'step2') {
			form.submit();
		} else {
			onFailureEvent && onFailureEvent();
		}
	};

	const handleOnFailureEvent = () => {
		if (step === 'step1') {
			onFailureEvent ? onFailureEvent() : onCloseModal();
		} else if (step === 'step2') {
			setStep('step1');
		} else {
			setStep('step2');
		}
	};

	return (
		<ModalContent
			onConsentEvent={handleOnConsentEvent}
			onFailureEvent={handleOnFailureEvent}
			onCloseModal={onFailureEvent}
			heading={['step1', 'step2'].includes(step) ? 'editCategorySubcategoryList' : 'validationInfo'}
			headerTranslationNameSpace="modals"
			translationOfConfirmBtn={translationOfConfirmBtn}
			isDisabledSave={step === 'step2' && !selectedRestaurants.length && !isApplyToOne}
			headingAlignment="left"
			centered={false}>
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				{['step1', 'step2'].includes(step) && (
					<Text translationKey={step} translationNameSpace="modals" size="xl" />
				)}
				{step === 'validationInfo' && (
					<Text
						translationKey="descriptionForValidationInfoOfAssignDishToSubcategory"
						translationNameSpace="modals"
						size="s"
					/>
				)}
				{step === 'step1' && (
					<>
						<div className={style.assignInfo}>
							<div>
								<FullInfoField label="customerLabel" />
								<Text translationKey={customerName} size="s" withTooltip={true} />
							</div>
							<div>
								<FullInfoField label="restaurantLabel" />
								<Text translationKey={restaurantName} size="s" withTooltip={true} />
							</div>
							<div>
								<FullInfoField label="dishLabel" />
								<Text translationKey={dishName} size="s" withTooltip={true} />
							</div>
						</div>
						<>
							{!!selectedCategoriesSubcategories.length &&
								allCategoriesResponse &&
								selectedCategoriesSubcategories.map((selectedCategorySubcategory) => (
									<CategorySubcategoryBlock
										key={selectedCategorySubcategory.uniqueId}
										setSelectedCategoriesSubcategories={setSelectedCategoriesSubcategories}
										fetchedCategories={allCategoriesResponse.data.categories}
										selectedCategoriesSubcategories={selectedCategoriesSubcategories}
										selectedCategorySubcategory={selectedCategorySubcategory}
										IDSelectedRestaurant={IDSelectedRestaurant}
										isLoading={allCategoriesLoading}
									/>
								))}
							{selectedCategoriesSubcategories.length <
								(allCategoriesResponse?.data.categories.length || 0) && (
								<Button
									className={style.editBtn}
									size="sizeS"
									theme="outlined"
									onClick={handleAddCategory}
									icon={
										<svg className={style.icon}>
											<use xlinkHref={`${breadcrumbs}#plus`} />
										</svg>
									}>
									<Text
										translationNameSpace="modals"
										translationKey="addCategoryButton"
										asFragment={true}
									/>
								</Button>
							)}
						</>
					</>
				)}
				{step === 'step2' && (
					<RestaurantsBlock
						setFetchedRestaurants={setFetchedRestaurants}
						setSelectedRestaurants={setSelectedRestaurants}
						toggleApplyToOne={toggleApplyToOne}
						selectedRestaurants={selectedRestaurants}
						isApplyToOne={isApplyToOne}
						IDSelectedRestaurant={IDSelectedRestaurant}
						customerName={customerName}
						dishName={dishName}
						dishId={dishId}
					/>
				)}
				{step === 'validationInfo' && validationInfo && (
					<ValidationInfo
						validationInfo={validationInfo}
						fetchedRestaurants={fetchedRestaurants}
						selectedRestaurants={selectedRestaurants}
					/>
				)}
			</Form>
		</ModalContent>
	);
};

export { ISelectedCategorySubcategory };
